import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { BrandLogo, FaeBillMark } from '../components/common/BrandLogo';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setEmailSent(true);
    } catch (err: any) {
      logger.error(err, 'password reset error');
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        setError(t('resetEmailError', 'Es wurde kein Konto mit dieser E-Mail-Adresse gefunden.'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('tooManyRequests', 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.'));
      } else {
        setError(t('genericError', 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center auth-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-4 left-4">
        <Link to="/" className="flex items-center">
          <BrandLogo className="h-10" />
        </Link>
      </div>

      <div className="bg-surface-raised p-8 rounded-lg shadow-xl w-full max-w-md border border-hairline">
        <div className="flex justify-center mb-6">
          <FaeBillMark className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-center text-primary">
          {t('resetPassword')}
        </h2>

        {emailSent ? (
          <div className="text-center">
            <div className="mb-6 mt-4 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] rounded-md p-4">
              <svg className="w-12 h-12 text-[var(--tone-success-fg)] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-[var(--tone-success-fg)] font-medium mb-2">
                {t('resetEmailSent', 'E-Mail wurde gesendet!')}
              </p>
              <p className="text-muted text-sm">
                {t('resetEmailSentDescription', 'Falls ein Konto mit dieser E-Mail-Adresse existiert, erhalten Sie in Kürze eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts.')}
              </p>
            </div>
            <Link
              to="/login"
              className="inline-block w-full bg-indigo-600 text-[#0C1322] py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors text-center"
            >
              {t('backToLogin')}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-muted text-sm text-center mb-6">
              {t('resetPasswordDescription', 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.')}
            </p>

            <form onSubmit={handleResetPassword}>
              {error && (
                <div className="mb-4 bg-[var(--tone-danger-bg)] border-l-4 border-[var(--tone-danger-border)] p-4">
                  <p className="text-primary text-sm">{error}</p>
                </div>
              )}
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1">
                  {t('email')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ihre@email.de"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 text-[#0C1322] py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {loading ? '...' : t('sendResetEmail')}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm">
          <Link to="/login" className="text-accent-ink hover:text-accent-ink transition-colors">
            {t('backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
